import { WEBSITE_GUEST_WISHLIST_KEY } from "src/constants/storageKeys";

export const GUEST_WISHLIST_CHANGED_EVENT = "guestWishlistChanged";
export const GUEST_WISHLIST_SYNC_STATE_CHANGED_EVENT = "guestWishlistSyncStateChanged";

export type GuestWishlistSyncState = "idle" | "syncing";

interface WishlistTarget {
    slug: string;
}

let guestWishlistSyncState: GuestWishlistSyncState = "idle";

const isBrowser = () => typeof window !== "undefined";

const dispatchWindowEvent = (eventName: string) => {
    if (!isBrowser()) {
        return;
    }

    window.dispatchEvent(new Event(eventName));
};

const normalizeGuestWishlist = (value: unknown): string[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return Array.from(
        new Set(
            value
                .filter((item): item is string => typeof item === "string")
                .map((item) => item.trim())
                .filter(Boolean),
        ),
    );
};

const writeGuestWishlist = (slugs: string[]) => {
    if (!isBrowser()) {
        return [];
    }

    const normalizedSlugs = normalizeGuestWishlist(slugs);

    if (normalizedSlugs.length) {
        localStorage.setItem(WEBSITE_GUEST_WISHLIST_KEY, JSON.stringify(normalizedSlugs));
    } else {
        localStorage.removeItem(WEBSITE_GUEST_WISHLIST_KEY);
    }

    dispatchWindowEvent(GUEST_WISHLIST_CHANGED_EVENT);
    return normalizedSlugs;
};

const getTargetSlug = (target: WishlistTarget | string) => (
    typeof target === "string" ? target : target.slug
);

export const getGuestWishlist = (): string[] => {
    if (!isBrowser()) {
        return [];
    }

    const storedValue = localStorage.getItem(WEBSITE_GUEST_WISHLIST_KEY);

    if (!storedValue) {
        return [];
    }

    try {
        const parsedValue = JSON.parse(storedValue);
        const normalizedValue = normalizeGuestWishlist(parsedValue);

        if (normalizedValue.length !== parsedValue?.length) {
            writeGuestWishlist(normalizedValue);
        }

        return normalizedValue;
    } catch (_error) {
        localStorage.removeItem(WEBSITE_GUEST_WISHLIST_KEY);
        dispatchWindowEvent(GUEST_WISHLIST_CHANGED_EVENT);
        return [];
    }
};

export const setGuestWishlist = (slugs: string[]) => writeGuestWishlist(slugs);

export const clearGuestWishlist = () => {
    writeGuestWishlist([]);
};

export const isProductInGuestWishlist = (target: WishlistTarget | string) => {
    const slug = getTargetSlug(target);
    return Boolean(slug) && getGuestWishlist().includes(slug);
};

export const addGuestWishlistItem = (target: WishlistTarget | string) => {
    const slug = getTargetSlug(target);

    if (!slug) {
        return getGuestWishlist();
    }

    return writeGuestWishlist([...getGuestWishlist(), slug]);
};

export const removeGuestWishlistItem = (target: WishlistTarget | string) => {
    const slug = getTargetSlug(target);

    if (!slug) {
        return getGuestWishlist();
    }

    return writeGuestWishlist(getGuestWishlist().filter((item) => item !== slug));
};

export const toggleGuestWishlistItem = (target: WishlistTarget | string) => {
    const slug = getTargetSlug(target);

    if (!slug) {
        return { inWishlist: false, slugs: getGuestWishlist() };
    }

    const alreadySaved = isProductInGuestWishlist(slug);
    const slugs = alreadySaved
        ? removeGuestWishlistItem(slug)
        : addGuestWishlistItem(slug);

    return {
        inWishlist: !alreadySaved,
        slugs,
    };
};

export const getGuestWishlistSyncState = () => guestWishlistSyncState;

export const setGuestWishlistSyncState = (state: GuestWishlistSyncState) => {
    guestWishlistSyncState = state;
    dispatchWindowEvent(GUEST_WISHLIST_SYNC_STATE_CHANGED_EVENT);
};
